/**
 * Markdown a HTML, para el chat y para el correo.
 *
 * No se usa una librería a propósito. Este texto puede llevar dentro trozos de
 * una tarjeta de Trello, del concepto de una factura o de un correo: contenido
 * que escribe cualquiera. Meterlo en el DOM es exactamente donde aparece un
 * XSS, y la historia de los sanitizadores es una lista larga de bypasses.
 *
 * Aquí el orden lo resuelve: **primero se escapa todo el HTML, y solo después
 * se transforma**. Cuando llega el momento de generar etiquetas, ya no queda
 * ningún `<` que pueda ser parte de una. No es que se filtre lo peligroso: es
 * que no puede llegar.
 *
 * Se soporta un subconjunto pequeño y deliberado: lo que el director usa de
 * verdad para presentar información —tablas, listas, negrita, código, enlaces—
 * y nada más. Menos superficie, menos formas de equivocarse.
 *
 * Este fichero NO importa nada de Node: lo usa también el navegador.
 */
/** Lo primero que se hace, siempre. */
function escapar(texto) {
    return texto
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
/**
 * Formato dentro de una línea: negrita, cursiva, código y enlaces.
 *
 * Recibe texto YA escapado. El código en línea se saca antes y se vuelve a
 * meter al final, para que `**esto**` dentro de un `código` no se convierta en
 * negrita: dentro de código, el markdown no manda.
 */
function enLinea(escapado) {
    const codigos = [];
    let texto = escapado.replace(/`([^`]+)`/g, (_, contenido) => {
        codigos.push(contenido);
        return ` ${codigos.length - 1} `;
    });
    // Enlaces: solo http y https. Un `javascript:` en un href es ejecución.
    texto = texto.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_, etiqueta, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${etiqueta}</a>`);
    texto = texto
        .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
        /*
         * La cursiva también empieza justo después de una etiqueta.
         *
         * El límite de delante era «principio de todo, espacio o paréntesis».
         * Dentro de un párrafo las líneas se unen con `<br>`, así que un `*Cómo:*`
         * al principio del segundo renglón va precedido de `>` y no encajaba: en el
         * parte de hoy salieron los asteriscos a pelo en cada bloque. Ese `>` sólo
         * puede venir de una etiqueta puesta por nosotros — el del texto de fuera
         * ya es `&gt;` desde el primer paso, que es escapar.
         */
        .replace(/(^|[\s(>])\*([^*\n]+)\*/g, "$1<em>$2</em>");
    return texto.replace(/ (\d+) /g, (_, i) => `<code>${codigos[Number(i)]}</code>`);
}
/** ¿Es la línea de guiones que separa la cabecera de una tabla? */
function esSeparadorDeTabla(linea) {
    return /^\s*\|?[\s:|-]*-[\s:|-]*\|?\s*$/.test(linea) && linea.includes("-");
}
function celdas(linea) {
    return linea
        .replace(/^\s*\|/, "")
        .replace(/\|\s*$/, "")
        .split("|")
        .map((c) => c.trim());
}
export function markdownAHtml(fuente, opciones = {}) {
    const desnivel = opciones.desnivel ?? 2;
    const lineas = escapar(fuente).split("\n");
    const salida = [];
    let i = 0;
    while (i < lineas.length) {
        const linea = lineas[i];
        // ── Bloque de código ────────────────────────────────────────────────
        if (/^\s*```/.test(linea)) {
            const cuerpo = [];
            i++;
            while (i < lineas.length && !/^\s*```/.test(lineas[i])) {
                cuerpo.push(lineas[i]);
                i++;
            }
            i++; // el cierre
            salida.push(`<pre><code>${cuerpo.join("\n")}</code></pre>`);
            continue;
        }
        // ── Tabla ───────────────────────────────────────────────────────────
        if (linea.includes("|") &&
            i + 1 < lineas.length &&
            esSeparadorDeTabla(lineas[i + 1])) {
            const cabecera = celdas(linea);
            i += 2;
            const filas = [];
            while (i < lineas.length && lineas[i].includes("|") && lineas[i].trim() !== "") {
                filas.push(celdas(lineas[i]));
                i++;
            }
            const th = cabecera.map((c) => `<th>${enLinea(c)}</th>`).join("");
            /*
             * Cada celda lleva el nombre de su columna en `data-campo`.
             *
             * En el móvil la tabla se despliega en vertical —una fila por campo— y
             * sin esto cada valor saldría suelto, sin saber de qué columna es. El
             * texto de la cabecera ya pasó por `escapar`, que también convierte las
             * comillas, así que vale tal cual dentro del atributo.
             */
            const tr = filas
                .map((f) => `<tr>${f
                .map((c, n) => `<td${cabecera[n] ? ` data-campo="${cabecera[n]}"` : ""}>${enLinea(c)}</td>`)
                .join("")}</tr>`)
                .join("");
            salida.push(`<table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table>`);
            continue;
        }
        // ── Títulos ─────────────────────────────────────────────────────────
        const titulo = /^(#{1,4})\s+(.*)$/.exec(linea);
        if (titulo) {
            const nivel = Math.min(Math.max(titulo[1].length + desnivel, 1), 6);
            salida.push(`<h${nivel}>${enLinea(titulo[2])}</h${nivel}>`);
            i++;
            continue;
        }
        // ── Listas ──────────────────────────────────────────────────────────
        const conNumero = /^\s*\d+[.)]\s+/.test(linea);
        const conViñeta = /^\s*[-*+]\s+/.test(linea);
        if (conNumero || conViñeta) {
            const etiqueta = conNumero ? "ol" : "ul";
            const patron = conNumero ? /^\s*\d+[.)]\s+/ : /^\s*[-*+]\s+/;
            const puntos = [];
            while (i < lineas.length && patron.test(lineas[i])) {
                puntos.push(`<li>${enLinea(lineas[i].replace(patron, ""))}</li>`);
                i++;
            }
            salida.push(`<${etiqueta}>${puntos.join("")}</${etiqueta}>`);
            continue;
        }
        // ── Línea en blanco ─────────────────────────────────────────────────
        if (linea.trim() === "") {
            i++;
            continue;
        }
        // ── Párrafo ─────────────────────────────────────────────────────────
        const parrafo = [];
        while (i < lineas.length &&
            lineas[i].trim() !== "" &&
            !/^\s*```/.test(lineas[i]) &&
            !/^(#{1,4})\s/.test(lineas[i]) &&
            !/^\s*\d+[.)]\s/.test(lineas[i]) &&
            !/^\s*[-*+]\s/.test(lineas[i])) {
            parrafo.push(lineas[i]);
            i++;
        }
        salida.push(`<p>${enLinea(parrafo.join("<br>"))}</p>`);
    }
    return salida.join("\n");
}
/**
 * Correo en HTML, con los estilos incrustados.
 *
 * Los clientes de correo ignoran las hojas de estilo externas y muchos también
 * las etiquetas <style>, así que el estilo va en cada elemento. Es feo de
 * escribir y es la única forma de que una tabla se vea como una tabla en
 * Gmail, en Outlook y en el móvil.
 */
export function correoEnHtml(titulo, markdown) {
    /**
     * El correo se lee en el móvil, y la app de correo del móvil manda más que tú.
     *
     * Primer intento: un div con fondo blanco declarado en CSS. No sirvió. La app
     * de Jorge impone su propio tema oscuro y reescribe los colores del mensaje,
     * así que el texto seguía saliendo gris sobre negro y apenas se leía.
     *
     * Contra eso hay dos cosas que funcionan y una que no:
     *
     *   · **No funciona** pelearse por el color. Si el cliente decide recolorear,
     *     recolorea. Cuanto más se insista, más raro queda en los que no lo hacen.
     *   · **Funciona** `bgcolor` como atributo de la tabla. Es de 1997 y por eso
     *     mismo lo respeta todo el mundo, incluidos los que tiran el CSS entero.
     *   · **Funciona siempre** la estructura: negritas, títulos, listas, tablas.
     *     Un cliente puede cambiarte el color; no te cambia lo que es un título.
     *     Por eso el peso está puesto ahí y no en la paleta.
     *
     * Y todo en atributos y estilos dentro de cada etiqueta, nunca en una hoja
     * aparte: media docena de clientes de correo las tiran sin avisar.
     */
    const cuerpo = markdownAHtml(markdown)
        .replace(/<table>/g, '<table role="presentation" style="border-collapse:collapse;width:100%;margin:16px 0">')
        .replace(/<th>/g, '<th style="text-align:left;padding:8px 10px;border-bottom:2px solid #c8ccd2;font-size:14px;font-weight:700">')
        .replace(/<td>/g, '<td style="padding:8px 10px;border-bottom:1px solid #dfe2e6;font-size:15px">')
        .replace(/<pre>/g, '<pre style="background:#f4f5f7;padding:12px 14px;border-radius:8px;overflow-x:auto;font-size:13px;line-height:1.55;margin:14px 0">')
        .replace(/<code>/g, '<code style="background:#eceef1;padding:1px 5px;border-radius:4px;font-size:13.5px">')
        /**
         * Los títulos van con peso y con aire, no con color.
         *
         * Es lo único que sobrevive a un cliente que recolorea, y es lo que de
         * verdad deja recorrer un aviso largo sin leerlo entero.
         */
        .replace(/<h1>/g, '<h1 style="margin:26px 0 10px;font-size:20px;line-height:1.3;font-weight:700">')
        .replace(/<h2>/g, '<h2 style="margin:24px 0 8px;font-size:17px;line-height:1.35;font-weight:700">')
        .replace(/<h(\d)>/g, '<h$1 style="margin:20px 0 8px;font-size:15.5px;font-weight:700">')
        .replace(/<p>/g, '<p style="margin:0 0 14px">')
        .replace(/<ul>/g, '<ul style="margin:0 0 14px;padding-left:22px">')
        .replace(/<ol>/g, '<ol style="margin:0 0 14px;padding-left:22px">')
        .replace(/<li>/g, '<li style="margin:0 0 7px">');
    const letra = "font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:16px;line-height:1.65";
    return [
        "<!doctype html><html><head>",
        '<meta charset="utf-8">',
        '<meta name="viewport" content="width=device-width,initial-scale=1">',
        '<meta name="color-scheme" content="light">',
        '<meta name="supported-color-schemes" content="light">',
        "</head>",
        '<body style="margin:0;padding:0;background:#eceef1">',
        // Tabla con bgcolor: es lo que respetan los clientes que se saltan el CSS.
        '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#eceef1" style="background:#eceef1">',
        '<tr><td align="center" style="padding:18px 12px">',
        '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="max-width:640px;background:#ffffff;border-radius:12px">',
        `<tr><td style="padding:22px 20px;${letra};color:#111111">`,
        cuerpo,
        '<p style="margin:26px 0 0;padding-top:14px;border-top:1px solid #dfe2e6;font-size:12px;line-height:1.5;color:#6b7280">',
        escapar(titulo),
        " · aviso automático de BILBOBOT 🤖</p>",
        "</td></tr></table>",
        "</td></tr></table>",
        "</body></html>",
    ].join("");
}
//# sourceMappingURL=markdown.js.map